import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const solucoesSubmenu = [
  { label: "Benefícios Corporativos", slug: "beneficios-corporativos", icone: "ri-building-2-line" },
  { label: "Plano de Saúde", slug: "plano-de-saude", icone: "ri-heart-pulse-line" },
  { label: "Plano Odontológico", slug: "plano-odontologico", icone: "ri-tooth-line" },
  { label: "Seguro de Vida", slug: "seguro-de-vida", icone: "ri-shield-user-line" },
  { label: "Previdência Privada", slug: "previdencia-privada", icone: "ri-safe-2-line" },
  { label: "Consultoria Estratégica", slug: "consultoria-estrategica", icone: "ri-line-chart-line" },
  { label: "Gestão de Contratos", slug: "gestao-de-contratos", icone: "ri-file-list-3-line" },
  { label: "Hub de Seguros", slug: "hub-de-seguros", icone: "ri-shield-star-line" },
  { label: "Mentores RH", slug: "mentores-rh", icone: "ri-user-star-line" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [solucoesOpen, setSolucoesOpen] = useState(false);
  const [mobileSolucoesOpen, setMobileSolucoesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === "/" || location.pathname === "";
  const isLandingPage = location.pathname.startsWith("/servicos/");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside + cleanup timeout on unmount
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSolucoesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Hover handlers with delay to prevent flicker / dead-zone
  const handleDropdownEnter = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSolucoesOpen(true);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setSolucoesOpen(false);
    }, 200);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setSolucoesOpen(false);
    setMobileSolucoesOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: "Início", href: "#inicio" },
    { label: "Quem somos", href: "#quem-somos" },
    { label: "Resultados", href: "#resultados" },
    { label: "Soluções", href: "#servicos", isDropdown: true },
    { label: "Seguros", href: "#hub-seguros" },
    { label: "Ecossistema", href: "#ecossistema" },
    { label: "Contato", href: "#contato" },
  ];

  const scrollTo = (href: string) => {
    setMenuOpen(false);
    setSolucoesOpen(false);
    setMobileSolucoesOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navigateToServico = (slug: string) => {
    setMenuOpen(false);
    setSolucoesOpen(false);
    setMobileSolucoesOpen(false);
    navigate(`/servicos/${slug}`);
  };

  // ── Color logic ──────────────────────────────────────────────
  // Home: always yellow bg → black text
  // Landing (not scrolled): transparent bg → white text
  // Landing (scrolled): yellow bg → black text
  const onYellow = isHomePage || scrolled;

  const linkColor = onYellow
    ? "text-gray-900 hover:text-gray-600"
    : "text-white/90 hover:text-[#FBC52D]";

  const logoFilter = onYellow
    ? "brightness-0"
    : "brightness-0 invert";

  // CTA Home: black bg + yellow text | Landing: yellow bg + white text
  const ctaClass = isHomePage
    ? "bg-black text-[#FBC52D] hover:bg-gray-900"
    : "bg-[#FBC52D] text-white hover:bg-[#e5b41c]";

  const hamburgerLine = onYellow ? "bg-gray-900" : "bg-white";

  const mobileMenuBg = onYellow
    ? "bg-[#FBC52D]/95 border-t border-[#FBC52D]/40"
    : "bg-[#111111] border-t border-white/8";

  const mobileLinkColor = onYellow ? "text-gray-900" : "text-white";
  const mobileLinkHover = onYellow ? "hover:text-gray-600" : "hover:text-white/70";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isHomePage
          ? "bg-[#FBC52D] backdrop-blur-md shadow-sm border-b border-[#FBC52D]/30"
          : scrolled
            ? "bg-[#FBC52D]/75 backdrop-blur-md shadow-sm border-b border-[#FBC52D]/30"
            : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-14 lg:h-16">
        {/* Logo */}
        <a
          href="#inicio"
          onClick={(e) => { e.preventDefault(); scrollTo("#inicio"); }}
          className="flex items-center cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/577ebfb2b9cf841907632e366e1dd697/1697ba7e5f2a4d4ef72f246478d823d0.png"
            alt="Ferrarezi Benefícios"
            className={`h-7 lg:h-8 w-auto object-contain transition-all duration-300 ${logoFilter}`}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-4">
          {navLinks.map((link) => {
            if (link.isDropdown) {
              return (
                <div
                  key={link.label}
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (solucoesOpen) {
                        setSolucoesOpen(false);
                      } else {
                        setSolucoesOpen(true);
                      }
                    }}
                    className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1 ${linkColor}`}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${solucoesOpen ? "rotate-180" : ""}`} />
                  </button>

                  {/* Dropdown */}
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 w-72 transition-all duration-200 origin-top ${
                      solucoesOpen
                        ? "opacity-100 scale-100 pointer-events-auto"
                        : "opacity-0 scale-95 pointer-events-none"
                    }`}
                  >
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden max-h-[70vh] flex flex-col">
                      <div className="py-2 overflow-y-auto overscroll-contain flex-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent">
                        {solucoesSubmenu.map((item) => (
                          <button
                            key={item.slug}
                            onClick={() => navigateToServico(item.slug)}
                            className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 hover:bg-[#F8F8F6] hover:text-black transition-colors cursor-pointer text-left whitespace-nowrap group"
                          >
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FBC52D]/10 group-hover:bg-[#FBC52D]/25 transition-colors">
                              <i className={`${item.icone} text-[#FBC52D] text-sm`} />
                            </div>
                            <span className="font-medium">{item.label}</span>
                          </button>
                        ))}
                      </div>
                      <div className="border-t border-gray-100 shrink-0">
                        <button
                          onClick={(e) => {
                            setSolucoesOpen(false);
                            scrollTo("#servicos");
                          }}
                          className="w-full flex items-center gap-3 px-5 py-3 text-sm text-[#FBC52D] font-semibold hover:bg-[#F8F8F6] transition-colors cursor-pointer text-left"
                        >
                          <i className="ri-grid-line text-base" />
                          <span>Ver todas as soluções</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollTo(link.href!); }}
                className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${linkColor}`}
              >
                {link.label}
              </a>
            );
          })}
        </nav>

        {/* CTA Button */}
        <div className="hidden md:flex items-center">
          <a
            href="#contato"
            onClick={(e) => { e.preventDefault(); scrollTo("#contato"); }}
            className={`whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200 cursor-pointer ${ctaClass}`}
          >
            Fale com nossos especialistas
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span className={`block w-6 h-0.5 transition-all duration-300 ${hamburgerLine} ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`block w-6 h-0.5 transition-all duration-300 ${hamburgerLine} ${menuOpen ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-0.5 transition-all duration-300 ${hamburgerLine} ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${mobileMenuBg} ${
          menuOpen ? "max-h-[800px] opacity-100 overflow-y-auto" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navLinks.map((link) => {
            if (link.isDropdown) {
              return (
                <div key={link.label}>
                  <button
                    onClick={() => setMobileSolucoesOpen(!mobileSolucoesOpen)}
                    className={`w-full flex items-center justify-between font-medium text-sm py-3 cursor-pointer transition-colors ${mobileLinkColor} ${mobileLinkHover}`}
                  >
                    <span>{link.label}</span>
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileSolucoesOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      mobileSolucoesOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className={`pl-4 py-1 flex flex-col gap-0 border-l ml-4 ${
                      onYellow ? "border-gray-300" : "border-white/15"
                    }`}>
                      {solucoesSubmenu.map((item) => (
                        <button
                          key={item.slug}
                          onClick={() => navigateToServico(item.slug)}
                          className={`flex items-center gap-3 text-sm py-2.5 cursor-pointer transition-colors text-left ${
                            onYellow
                              ? "text-gray-600 hover:text-gray-900"
                              : "text-white/70 hover:text-white"
                          }`}
                        >
                          <i className={`${item.icone} text-base`} />
                          <span>{item.label}</span>
                        </button>
                      ))}
                      <button
                        onClick={(e) => {
                          setMobileSolucoesOpen(false);
                          scrollTo("#servicos");
                        }}
                        className="flex items-center gap-3 text-black text-sm py-2.5 font-semibold cursor-pointer hover:text-gray-700 text-left"
                      >
                        <i className="ri-grid-line text-base" />
                        <span>Ver todas as soluções</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollTo(link.href!); }}
                className={`font-medium text-sm py-3 cursor-pointer transition-colors ${mobileLinkColor} ${mobileLinkHover}`}
              >
                {link.label}
              </a>
            );
          })}
          <a
            href="#contato"
            onClick={(e) => { e.preventDefault(); scrollTo("#contato"); }}
            className={`whitespace-nowrap text-sm font-semibold px-5 py-3 rounded-full text-center cursor-pointer transition-colors mt-3 ${ctaClass}`}
          >
            Fale com nossos especialistas
          </a>
        </div>
      </div>
    </header>
  );
}